import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import "./LocationSearch.scss";

const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

function formatEntityMeta(row) {
  const parts = [];
  if (row.pincode) parts.push(`Pin ${row.pincode}`);
  if (row.locationLabel) parts.push(row.locationLabel);
  if (row.distanceKm != null && row.distanceKm !== "") {
    parts.push(`${row.distanceKm} km`);
  }
  return parts.join(" · ");
}

function ResultRow({ row, type, entityLabel, onSelect }) {
  const meta = formatEntityMeta(row);
  return (
    <li>
      <button type="button" className="loc_search_row" onClick={() => onSelect(type, row.id)}>
        <span className={`loc_search_badge loc_search_badge--${type}`}>{entityLabel}</span>
        <span className="loc_search_row_body">
          <span className="loc_search_row_name">{row.name}</span>
          {meta ? <span className="loc_search_row_meta">{meta}</span> : null}
        </span>
        <span className="loc_search_row_chevron" aria-hidden>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </button>
    </li>
  );
}

export default function LocationSearch() {
  const navigate = useNavigate();

  const [mainTab, setMainTab] = useState("nearby");

  const [radiusKm, setRadiusKm] = useState(25);
  const [coords, setCoords] = useState(null);
  const [centerLabel, setCenterLabel] = useState("");

  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  const [placeQuery, setPlaceQuery] = useState("");
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState("");

  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyResults, setNearbyResults] = useState(null);
  const [filterText, setFilterText] = useState("");

  const [textMode, setTextMode] = useState("all");
  const [textQ, setTextQ] = useState("");
  const [textLoading, setTextLoading] = useState(false);
  const [textResults, setTextResults] = useState(null);

  const goToEntity = (type, id) => {
    const highlight = `${type}-${id}`;
    if (type === "patient") {
      navigate(`/patient-list?highlight=${encodeURIComponent(highlight)}`);
      return;
    }
    if (type === "doctor") {
      navigate(`/doctor-list?highlight=${encodeURIComponent(highlight)}`);
      return;
    }
    if (type === "hospital") {
      navigate(`/hospital-list?highlight=${encodeURIComponent(highlight)}`);
    }
  };

  const requestDeviceLocation = useCallback(() => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("This browser does not support location.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setNearbyResults(null);
        setCenterLabel("Looking up address…");
        (async () => {
          try {
            const res = await API.get(endpoints.ADMIN_REVERSE_GEOCODE, {
              params: { lat, lng },
            });
            const data = res?.data?.data ?? res?.data;
            const label =
              data && typeof data.displayName === "string" && data.displayName.trim()
                ? data.displayName.trim()
                : `Near ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
            setCenterLabel(label);
          } catch {
            setCenterLabel(`Near ${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          } finally {
            setGeoLoading(false);
          }
        })();
      },
      (err) => {
        setGeoLoading(false);
        const code = err && err.code;
        if (code === 1) {
          setGeoError(
            "Location was blocked. Tap the lock icon in the address bar to allow access, or search for a place below."
          );
        } else if (code === 2) {
          setGeoError("Position unavailable. Try again or search for a place below.");
        } else if (code === 3) {
          setGeoError("Request timed out. Try again.");
        } else {
          setGeoError(err.message || "Could not read your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 25000, maximumAge: 0 }
    );
  }, []);

  const lookupPlace = async () => {
    const q = placeQuery.trim();
    if (q.length < 2) {
      setGeocodeError("Enter at least 2 characters.");
      return;
    }
    setGeocodeError("");
    setGeocodeLoading(true);
    try {
      const res = await API.get(endpoints.ADMIN_GEOCODE, { params: { q } });
      const data = res?.data?.data ?? res?.data;
      if (!data || typeof data.lat !== "number" || typeof data.lng !== "number") {
        setGeocodeError("Unexpected response.");
        return;
      }
      setCoords({ lat: data.lat, lng: data.lng });
      setCenterLabel(data.displayName || q);
      setNearbyResults(null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not find that place.";
      setGeocodeError(msg);
    } finally {
      setGeocodeLoading(false);
    }
  };

  const runNearbySearch = async () => {
    if (!coords) return;
    const rKm = Math.min(500, Math.max(1, Number(radiusKm) || 25));
    setNearbyLoading(true);
    setNearbyResults(null);
    try {
      const params = {
        mode: "radius",
        lat: coords.lat,
        lng: coords.lng,
        radiusKm: rKm,
      };
      const ft = filterText.trim();
      if (ft.length >= 2) params.q = ft;
      const res = await API.get(endpoints.ADMIN_SEARCH, { params });
      const data = res?.data?.data ?? res?.data ?? {};
      setNearbyResults({
        doctors: data.doctors || [],
        hospitals: data.hospitals || [],
        patients: data.patients || [],
      });
    } catch {
      setNearbyResults({ doctors: [], hospitals: [], patients: [] });
    } finally {
      setNearbyLoading(false);
    }
  };

  const runTextSearch = async () => {
    const q = textQ.trim();
    if (q.length < 2) {
      setTextResults(null);
      return;
    }
    setTextLoading(true);
    setTextResults(null);
    try {
      const res = await API.get(endpoints.ADMIN_SEARCH, {
        params: textMode === "all" ? { q } : { q, mode: textMode },
      });
      const data = res?.data?.data ?? res?.data ?? {};
      setTextResults({
        doctors: data.doctors || [],
        hospitals: data.hospitals || [],
        patients: data.patients || [],
      });
    } catch {
      setTextResults({ doctors: [], hospitals: [], patients: [] });
    } finally {
      setTextLoading(false);
    }
  };

  const nearbyTotal = useMemo(() => {
    if (!nearbyResults) return 0;
    return (
      (nearbyResults.doctors?.length || 0) +
      (nearbyResults.hospitals?.length || 0) +
      (nearbyResults.patients?.length || 0)
    );
  }, [nearbyResults]);

  const textTotal = useMemo(() => {
    if (!textResults) return 0;
    return (
      (textResults.doctors?.length || 0) +
      (textResults.hospitals?.length || 0) +
      (textResults.patients?.length || 0)
    );
  }, [textResults]);

  const hasNearby = nearbyTotal > 0;
  const hasText = textTotal > 0;

  const renderNearbyResults = () => {
    if (!nearbyResults) return null;
    if (!hasNearby) {
      return (
        <div className="loc_search_empty" role="status">
          <p className="loc_search_empty_title">No one in this radius</p>
          <p className="loc_search_empty_hint">Try a larger radius or clear the optional filter.</p>
        </div>
      );
    }
    return (
      <div className="loc_search_results">
        <p className="loc_search_results_count">{nearbyTotal} result{nearbyTotal === 1 ? "" : "s"}</p>
        {nearbyResults.doctors?.length ? (
          <div className="loc_search_block">
            <h3 className="loc_search_block_title">Doctors</h3>
            <ul className="loc_search_list">
              {nearbyResults.doctors.map((row) => (
                <ResultRow key={`doctor-${row.id}`} row={row} type="doctor" entityLabel="Doctor" onSelect={goToEntity} />
              ))}
            </ul>
          </div>
        ) : null}
        {nearbyResults.hospitals?.length ? (
          <div className="loc_search_block">
            <h3 className="loc_search_block_title">Hospitals</h3>
            <ul className="loc_search_list">
              {nearbyResults.hospitals.map((row) => (
                <ResultRow
                  key={`hospital-${row.id}`}
                  row={row}
                  type="hospital"
                  entityLabel="Hospital"
                  onSelect={goToEntity}
                />
              ))}
            </ul>
          </div>
        ) : null}
        {nearbyResults.patients?.length ? (
          <div className="loc_search_block">
            <h3 className="loc_search_block_title">Patients</h3>
            <ul className="loc_search_list">
              {nearbyResults.patients.map((row) => (
                <ResultRow
                  key={`patient-${row.id}`}
                  row={row}
                  type="patient"
                  entityLabel="Patient"
                  onSelect={goToEntity}
                />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  const renderTextResults = () => {
    if (!textResults) return null;
    if (!hasText) {
      return (
        <div className="loc_search_empty" role="status">
          <p className="loc_search_empty_title">No matches</p>
          <p className="loc_search_empty_hint">Try different words or switch the search mode above.</p>
        </div>
      );
    }
    return (
      <div className="loc_search_results">
        <p className="loc_search_results_count">{textTotal} result{textTotal === 1 ? "" : "s"}</p>
        {textResults.doctors?.length ? (
          <div className="loc_search_block">
            <h3 className="loc_search_block_title">Doctors</h3>
            <ul className="loc_search_list">
              {textResults.doctors.map((row) => (
                <ResultRow key={`doctor-${row.id}`} row={row} type="doctor" entityLabel="Doctor" onSelect={goToEntity} />
              ))}
            </ul>
          </div>
        ) : null}
        {textResults.hospitals?.length ? (
          <div className="loc_search_block">
            <h3 className="loc_search_block_title">Hospitals</h3>
            <ul className="loc_search_list">
              {textResults.hospitals.map((row) => (
                <ResultRow
                  key={`hospital-${row.id}`}
                  row={row}
                  type="hospital"
                  entityLabel="Hospital"
                  onSelect={goToEntity}
                />
              ))}
            </ul>
          </div>
        ) : null}
        {textResults.patients?.length ? (
          <div className="loc_search_block">
            <h3 className="loc_search_block_title">Patients</h3>
            <ul className="loc_search_list">
              {textResults.patients.map((row) => (
                <ResultRow
                  key={`patient-${row.id}`}
                  row={row}
                  type="patient"
                  entityLabel="Patient"
                  onSelect={goToEntity}
                />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="loc_search_page">
        <header className="loc_search_hero">
          <h1 className="loc_search_page_title">Location search</h1>
          <p className="loc_search_lead">
            Search by distance from you or a place, or match names and areas in the database.
          </p>
        </header>

        <div className="loc_search_tabbar" role="tablist" aria-label="Search mode">
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "nearby"}
            className={`loc_search_tab${mainTab === "nearby" ? " is-active" : ""}`}
            onClick={() => setMainTab("nearby")}
          >
            <span className="loc_search_tab_icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            </span>
            Nearby
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "text"}
            className={`loc_search_tab${mainTab === "text" ? " is-active" : ""}`}
            onClick={() => setMainTab("text")}
          >
            <span className="loc_search_tab_icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </span>
            Text search
          </button>
        </div>

        {mainTab === "nearby" && (
          <section className="loc_search_card loc_search_card--radius" aria-labelledby="loc-nearby-title">
            <h2 id="loc-nearby-title" className="loc_search_card_title">
              Search nearby
            </h2>
            <p className="loc_search_card_desc">Choose a center point, then pick how far to search.</p>

            <div className="loc_search_split">
              <div className="loc_search_panel">
                <div className="loc_search_panel_head">
                  <span className="loc_search_panel_icon loc_search_panel_icon--pin" aria-hidden>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" />
                      <circle cx="12" cy="10" r="2.5" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="loc_search_panel_title">Use my location</h3>
                    <p className="loc_search_panel_hint">Allow location when the browser asks.</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="loc_search_btn loc_search_btn--primary loc_search_btn--block"
                  onClick={requestDeviceLocation}
                  disabled={geoLoading}
                >
                  {geoLoading ? (
                    <>
                      <span className="loc_search_spinner loc_search_spinner--on-primary" aria-hidden />
                      Getting location…
                    </>
                  ) : (
                    "Use current location"
                  )}
                </button>
                {geoError ? <p className="loc_search_err">{geoError}</p> : null}
              </div>

              <div className="loc_search_panel">
                <div className="loc_search_panel_head">
                  <span className="loc_search_panel_icon loc_search_panel_icon--map" aria-hidden>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
                      <path d="M9 3v15M15 6v15" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="loc_search_panel_title">Search a place</h3>
                    <p className="loc_search_panel_hint">City, area, or pincode.</p>
                  </div>
                </div>
                <div className="loc_search_place_row">
                  <input
                    type="search"
                    enterKeyHint="search"
                    autoComplete="street-address"
                    className="loc_search_input"
                    value={placeQuery}
                    onChange={(e) => setPlaceQuery(e.target.value)}
                    placeholder="e.g. Indiranagar, 560038"
                    onKeyDown={(e) => e.key === "Enter" && lookupPlace()}
                  />
                  <button
                    type="button"
                    className="loc_search_btn loc_search_btn--secondary loc_search_btn--grow"
                    onClick={lookupPlace}
                    disabled={geocodeLoading || placeQuery.trim().length < 2}
                  >
                    {geocodeLoading ? (
                      <>
                        <span className="loc_search_spinner loc_search_spinner--on-secondary" aria-hidden />
                        Looking up…
                      </>
                    ) : (
                      "Look up"
                    )}
                  </button>
                </div>
                {geocodeError ? <p className="loc_search_err">{geocodeError}</p> : null}
              </div>
            </div>

            {coords ? (
              <div className="loc_search_center">
                <div className="loc_search_center_pin" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" />
                    <circle cx="12" cy="10" r="2" />
                  </svg>
                </div>
                <div className="loc_search_center_text">
                  <span className="loc_search_center_kicker">Search center</span>
                  <p className="loc_search_center_address">{centerLabel || "Selected point"}</p>
                </div>

                <div className="loc_search_step">
                  <span className="loc_search_step_label">Radius</span>
                  <div className="loc_search_radius_pills" role="group" aria-label="Search radius in kilometers">
                    {RADIUS_OPTIONS.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={`loc_search_pill${radiusKm === k ? " is-active" : ""}`}
                        onClick={() => setRadiusKm(k)}
                      >
                        {k} km
                      </button>
                    ))}
                  </div>
                </div>

                <label className="loc_search_filter_label" htmlFor="loc-filter">
                  Optional filter
                </label>
                <p className="loc_search_filter_hint">Name, city, or pincode (min. 2 characters)</p>
                <input
                  id="loc-filter"
                  type="text"
                  className="loc_search_input loc_search_input--full"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Narrow results…"
                  autoComplete="off"
                />

                <button
                  type="button"
                  className="loc_search_btn loc_search_btn--primary loc_search_btn--block loc_search_btn--cta"
                  onClick={runNearbySearch}
                  disabled={nearbyLoading}
                >
                  {nearbyLoading ? (
                    <>
                      <span className="loc_search_spinner loc_search_spinner--on-primary" aria-hidden />
                      Searching…
                    </>
                  ) : (
                    "Search nearby"
                  )}
                </button>
              </div>
            ) : (
              <p className="loc_search_hint_below">Set a center with one of the options above to continue.</p>
            )}

            {nearbyLoading && !nearbyResults ? (
              <div className="loc_search_loading_banner" role="status" aria-live="polite">
                <span className="loc_search_spinner loc_search_spinner--muted" />
                Finding doctors, hospitals, and patients…
              </div>
            ) : null}

            {nearbyResults ? renderNearbyResults() : null}
          </section>
        )}

        {mainTab === "text" && (
          <section className="loc_search_card" aria-labelledby="loc-text-title">
            <h2 id="loc-text-title" className="loc_search_card_title">
              Text search
            </h2>
            <p className="loc_search_card_desc">No map or distance — match what is stored on profiles.</p>

            <div className="loc_search_segmented" role="group" aria-label="Text search mode">
              <button
                type="button"
                className={textMode === "all" ? "is-active" : ""}
                onClick={() => setTextMode("all")}
              >
                All fields
              </button>
              <button
                type="button"
                className={textMode === "location" ? "is-active" : ""}
                onClick={() => setTextMode("location")}
              >
                City / area
              </button>
              <button
                type="button"
                className={textMode === "pincode" ? "is-active" : ""}
                onClick={() => setTextMode("pincode")}
              >
                Pincode
              </button>
            </div>

            <div className="loc_search_place_row loc_search_place_row--text">
              <input
                id="loc-text-q"
                type="search"
                enterKeyHint="search"
                className="loc_search_input"
                value={textQ}
                onChange={(e) => setTextQ(e.target.value)}
                placeholder={
                  textMode === "pincode"
                    ? "Pincode…"
                    : textMode === "location"
                      ? "City, state, area…"
                      : "Name, city, pincode…"
                }
                onKeyDown={(e) => e.key === "Enter" && runTextSearch()}
              />
              <button
                type="button"
                className="loc_search_btn loc_search_btn--primary loc_search_btn--grow"
                onClick={runTextSearch}
                disabled={textLoading || textQ.trim().length < 2}
              >
                {textLoading ? (
                  <>
                    <span className="loc_search_spinner loc_search_spinner--on-primary" aria-hidden />
                    Searching…
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>

            {textLoading && !textResults ? (
              <div className="loc_search_loading_banner" role="status" aria-live="polite">
                <span className="loc_search_spinner loc_search_spinner--muted" />
                Searching…
              </div>
            ) : null}

            {textResults ? renderTextResults() : null}
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
